/*
 * The dock layout, as data.
 *
 * A workspace is a tree: `split` nodes divide space, `group` nodes hold documents
 * as tabs. Everything here is pure — no React, no DOM, no direction awareness —
 * so the layout can be reasoned about, tested, and written to storage on its own.
 *
 * Positions are logical (`start`/`end`), not physical (`left`/`right`). A row is
 * laid out by flexbox, which already flows the other way in Persian, so the tree
 * needs no mirroring: only the pointer maths at the drag layer converts a physical
 * screen position into one of these logical zones.
 */

/** Where a dragged document lands relative to the target it was released on. */
export type DropZone = 'center' | 'start' | 'end' | 'top' | 'bottom';

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface DockGroupNode {
  readonly kind: 'group';
  readonly id: string;
  readonly panelIds: readonly string[];
  readonly activePanelId: string | null;
}

export interface DockSplitNode {
  readonly kind: 'split';
  readonly id: string;
  readonly direction: 'row' | 'column';
  readonly children: readonly DockNode[];
  /** One fraction per child, always summing to 1. */
  readonly sizes: readonly number[];
}

export type DockNode = DockGroupNode | DockSplitNode;

/** No pane may be squeezed below this share of its split, so a splitter can always be grabbed back. */
export const minimumFraction = 0.08;

let sequence = 0;

/** Ids only have to be unique within one browser tab, and readable while debugging. */
export function nextDockId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${sequence.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function isGroup(node: DockNode): node is DockGroupNode {
  return node.kind === 'group';
}

export function createGroup(panelIds: readonly string[] = [], activePanelId: string | null = null, id = nextDockId('group')): DockGroupNode {
  return {
    kind: 'group',
    id,
    panelIds: [...panelIds],
    activePanelId: activePanelId ?? panelIds[panelIds.length - 1] ?? null,
  };
}

export function collectGroups(node: DockNode): DockGroupNode[] {
  if (isGroup(node)) return [node];
  return node.children.flatMap(collectGroups);
}

export function findGroup(node: DockNode, groupId: string): DockGroupNode | undefined {
  return collectGroups(node).find((group) => group.id === groupId);
}

export function findPanelGroup(node: DockNode, panelId: string): DockGroupNode | undefined {
  return collectGroups(node).find((group) => group.panelIds.includes(panelId));
}

export function collectPanelIds(node: DockNode): string[] {
  return collectGroups(node).flatMap((group) => [...group.panelIds]);
}

function evenSizes(count: number): number[] {
  return Array.from({ length: count }, () => 1 / count);
}

/** Rescales any set of positive weights back to fractions of one. */
export function normalizeSizes(sizes: readonly number[]): number[] {
  const total = sizes.reduce((sum, size) => sum + Math.max(size, 0), 0);
  if (total <= 0) return evenSizes(sizes.length);
  return sizes.map((size) => Math.max(size, 0) / total);
}

/**
 * Collapses the shapes a tree can fall into after an edit: a split with nothing left in
 * it disappears, a split down to one child is replaced by that child, and a split nested
 * directly inside a split of the same direction is flattened into its parent — so three
 * panes side by side stay one row rather than a chain of nested pairs.
 */
export function normalizeNode(node: DockNode): DockNode | null {
  if (isGroup(node)) return node;

  const surviving: { child: DockNode; size: number }[] = [];
  node.children.forEach((child, index) => {
    const normalized = normalizeNode(child);
    if (normalized === null) return;
    surviving.push({ child: normalized, size: node.sizes[index] ?? 1 / node.children.length });
  });

  const children: DockNode[] = [];
  const sizes: number[] = [];
  for (const { child, size } of surviving) {
    if (!isGroup(child) && child.direction === node.direction) {
      child.children.forEach((grandChild, index) => {
        children.push(grandChild);
        sizes.push(size * (child.sizes[index] ?? 1 / child.children.length));
      });
      continue;
    }
    children.push(child);
    sizes.push(size);
  }

  if (children.length === 0) return null;
  const only = children[0];
  if (children.length === 1 && only !== undefined) return only;
  return { ...node, children, sizes: normalizeSizes(sizes) };
}

type NodeTransform = (node: DockNode) => DockNode | null;

/** Rebuilds the tree with `transform` applied to the node carrying `id`. */
function replaceNode(node: DockNode, id: string, transform: NodeTransform): DockNode | null {
  if (node.id === id) {
    const replaced = transform(node);
    return replaced === null ? null : normalizeNode(replaced);
  }
  if (isGroup(node)) return node;

  let changed = false;
  const children: DockNode[] = [];
  const sizes: number[] = [];
  node.children.forEach((child, index) => {
    const next = replaceNode(child, id, transform);
    if (next !== child) changed = true;
    if (next === null) return;
    children.push(next);
    sizes.push(node.sizes[index] ?? 1 / node.children.length);
  });

  if (!changed) return node;
  if (children.length === 0) return null;
  return normalizeNode({ ...node, children, sizes: normalizeSizes(sizes) });
}

/** Adds a document to a group's tab strip and brings it forward. */
export function insertPanel(root: DockNode, groupId: string, panelId: string, index?: number): DockNode {
  const next = replaceNode(root, groupId, (node) => {
    if (!isGroup(node)) return node;
    const panelIds = node.panelIds.filter((id) => id !== panelId);
    const at = index === undefined ? panelIds.length : Math.max(0, Math.min(index, panelIds.length));
    panelIds.splice(at, 0, panelId);
    return { ...node, panelIds, activePanelId: panelId };
  });
  return next ?? root;
}

/** Moves a tab within its own strip, for drag-to-reorder. */
export function reorderPanel(root: DockNode, groupId: string, panelId: string, index: number): DockNode {
  return insertPanel(root, groupId, panelId, index);
}

/**
 * Takes a document out of the tree. The pane it leaves behind survives only while it
 * still holds something — except for the last pane standing, which stays as the empty
 * canvas the start page is drawn on.
 */
export function removePanel(root: DockNode, panelId: string): DockNode {
  const owner = findPanelGroup(root, panelId);
  if (owner === undefined) return root;
  const isLastGroup = collectGroups(root).length === 1;

  const next = replaceNode(root, owner.id, (node) => {
    if (!isGroup(node)) return node;
    const panelIds = node.panelIds.filter((id) => id !== panelId);
    if (panelIds.length === 0 && !isLastGroup) return null;
    const fallbackIndex = Math.min(node.panelIds.indexOf(panelId), panelIds.length - 1);
    const activePanelId = node.activePanelId === panelId ? panelIds[Math.max(fallbackIndex, 0)] ?? null : node.activePanelId;
    return { ...node, panelIds, activePanelId };
  });

  return next ?? createGroup();
}

/** Removes a whole pane — the "close every tab here" gesture. */
export function removeGroup(root: DockNode, groupId: string): DockNode {
  if (collectGroups(root).length === 1) return createGroup();
  return replaceNode(root, groupId, () => null) ?? createGroup();
}

export function activatePanelIn(root: DockNode, panelId: string): DockNode {
  const owner = findPanelGroup(root, panelId);
  if (owner === undefined || owner.activePanelId === panelId) return root;
  return replaceNode(root, owner.id, (node) => (isGroup(node) ? { ...node, activePanelId: panelId } : node)) ?? root;
}

function splitWith(existing: DockNode, incoming: DockNode, zone: Exclude<DropZone, 'center'>): DockSplitNode {
  const direction = zone === 'start' || zone === 'end' ? 'row' : 'column';
  const incomingFirst = zone === 'start' || zone === 'top';
  return {
    kind: 'split',
    id: nextDockId('split'),
    direction,
    children: incomingFirst ? [incoming, existing] : [existing, incoming],
    sizes: [0.5, 0.5],
  };
}

/**
 * Drops a document onto a pane: into its tabs when the pointer was over the middle, or
 * into a fresh pane beside it when the pointer was near one of its edges.
 */
export function dropPanelOnGroup(root: DockNode, targetGroupId: string, panelId: string, zone: DropZone, index?: number): DockNode {
  const detached = removePanel(root, panelId);
  if (findGroup(detached, targetGroupId) === undefined) {
    // The document was the last tab of the pane it was dropped on, and that pane left with it.
    const fallback = collectGroups(detached)[0];
    return fallback === undefined ? createGroup([panelId], panelId) : insertPanel(detached, fallback.id, panelId, index);
  }
  if (zone === 'center') {
    // A tab dragged along its own strip vacates a place before it takes one, so an index
    // measured to its right has to come back by one or the tab lands short of the caret.
    const source = findPanelGroup(root, panelId);
    const from = source?.id === targetGroupId ? source.panelIds.indexOf(panelId) : -1;
    const landing = index !== undefined && from >= 0 && from < index ? index - 1 : index;
    return insertPanel(detached, targetGroupId, panelId, landing);
  }

  const next = replaceNode(detached, targetGroupId, (node) => splitWith(node, createGroup([panelId], panelId), zone));
  return next ?? detached;
}

/** Docks a document against an outer edge of the whole canvas. */
export function dropPanelOnRoot(root: DockNode, panelId: string, zone: Exclude<DropZone, 'center'>): DockNode {
  const detached = removePanel(root, panelId);
  if (isGroup(detached) && detached.panelIds.length === 0) return createGroup([panelId], panelId, detached.id);
  return normalizeNode(splitWith(detached, createGroup([panelId], panelId), zone)) ?? detached;
}

/** Applies a splitter drag, keeping every pane above the minimum share. */
export function resizeSplit(root: DockNode, splitId: string, index: number, fraction: number): DockNode {
  const next = replaceNode(root, splitId, (node) => {
    if (isGroup(node)) return node;
    const before = node.sizes[index] ?? 0;
    const after = node.sizes[index + 1] ?? 0;
    const pair = before + after;
    if (pair <= 0) return node;
    const clamped = Math.max(minimumFraction, Math.min(fraction, pair - minimumFraction));
    const sizes = [...node.sizes];
    sizes[index] = clamped;
    sizes[index + 1] = pair - clamped;
    return { ...node, sizes: normalizeSizes(sizes) };
  });
  return next ?? root;
}

/** True when the workspace holds nothing at all — the state the start page answers. */
export function isEmptyLayout(root: DockNode): boolean {
  return collectPanelIds(root).length === 0;
}

/**
 * Turns a pointer position inside a pane into the zone it would dock to. The middle
 * two-thirds tabify; the outer band splits. `rtl` flips the horizontal halves so the
 * physical edge under the pointer maps to the logical end of the row it will join.
 */
export function zoneFromPoint(rect: Rect, x: number, y: number, rtl: boolean, edgeRatio = 0.28): DropZone {
  const relativeX = (x - rect.x) / Math.max(rect.width, 1);
  const relativeY = (y - rect.y) / Math.max(rect.height, 1);
  const horizontal = Math.min(relativeX, 1 - relativeX);
  const vertical = Math.min(relativeY, 1 - relativeY);
  if (horizontal > edgeRatio && vertical > edgeRatio) return 'center';
  if (horizontal <= vertical) {
    const physicalStart = relativeX < 0.5;
    return physicalStart === !rtl ? 'start' : 'end';
  }
  return relativeY < 0.5 ? 'top' : 'bottom';
}
